/**
 * The containment tree, and the one thing it does that a table cannot.
 *
 * `GET /api/design` does not simply list what each declaration holds. It
 * resolves every usage to the definition it is typed by and lends that
 * definition's children and PORTS to the usage - so `part sek : Seeker` shows
 * Seeker's internals instead of looking empty, which is the difference between a
 * design somebody can walk and a flat list of names. That is stated on the
 * screen, in `TREE_NOTE`, because nothing about the picture reveals it: the rows
 * under a usage are declared somewhere else entirely in the file.
 *
 * Rows arrive flat with a depth; the nesting is rebuilt by `buildDesignTree`,
 * which is pure and tested apart from this file for the reason given there.
 *
 * Each row also carries its ROLLED-UP quantities, which is what makes the tree a
 * budget rather than a picture: "what does this subtree weigh" is the question
 * somebody reading a decomposition actually has, and the answer belongs on the
 * row it is about. The columns are handed in - they come from the payload's own
 * `attributes`, because which quantities aggregate is declared in the model and a
 * column named here would defeat that. The wording, including when a `≥` appears,
 * is decided in `rollup-model.ts`.
 */

import { Link as RouterLink } from 'react-router-dom'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { MONOSPACE } from '../../theme'
import { allDesignKeys } from './parts-model'
import type { DesignNode } from './parts-model'
import { TotalsSummary } from './PartTotals'
import type { RollupColumn } from './rollup-model'

export const TREE_NOTE =
  'Containment, walked through the definitions. A usage is shown with the ' +
  'children and ports of the definition it is typed by — so `part sek : Seeker` ' +
  'lists Seeker’s internals rather than looking empty, even though those are ' +
  'declared on Seeker and not at the usage. That is what this view does and the ' +
  'definitions table cannot.'

export default function DesignTree({
  nodes,
  selected,
  onSelect,
  onOpen,
  columns,
}: {
  nodes: DesignNode[]
  selected: string
  onSelect: (id: string) => void
  /** Open the full editor for a row - a double-click, as in the table. */
  onOpen: (id: string) => void
  /** Which quantities to total per row, from the payload. Empty is a valid answer. */
  columns: readonly RollupColumn[]
}) {
  return (
    <SimpleTreeView
      // Keyed on the shape so a filtered tree re-expands rather than keeping
      // the expansion of the tree it replaced.
      key={allDesignKeys(nodes).length}
      defaultExpandedItems={allDesignKeys(nodes)}
      sx={{ '& .MuiTreeItem-content': { py: 0.35 } }}
    >
      {nodes.map((node) => (
        <Branch
          key={node.key}
          node={node}
          selected={selected}
          onSelect={onSelect}
          onOpen={onOpen}
          columns={columns}
        />
      ))}
    </SimpleTreeView>
  )
}

function Branch({
  node,
  selected,
  onSelect,
  onOpen,
  columns,
}: {
  node: DesignNode
  selected: string
  onSelect: (id: string) => void
  onOpen: (id: string) => void
  columns: readonly RollupColumn[]
}) {
  const { row } = node

  return (
    <TreeItem
      itemId={node.key}
      onClick={() => onSelect(row.id)}
      // The same gesture as the table's: a click selects, a double-click opens
      // the editor. Stopped from bubbling, or every ancestor row would open too.
      onDoubleClick={(event) => {
        event.stopPropagation()
        onOpen(row.id)
      }}
      label={
        <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.25 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: row.id === selected ? 600 : 400,
              }}
            >
              {row.name || row.id}
            </Typography>
            {/* The ID as a link, so the tree reaches the element's own page. */}
            <RouterLink
              to={`/parts/${encodeURIComponent(row.id)}`}
              style={{ fontFamily: MONOSPACE, fontSize: '0.8rem' }}
              onClick={(event) => event.stopPropagation()}
            >
              {row.id}
            </RouterLink>
            {row.type_name && (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: MONOSPACE }}>
                : {row.type_name}
              </Typography>
            )}
            {row.multiplicity && (
              <Chip label={row.multiplicity} size="small" variant="outlined" sx={{ height: 20 }} />
            )}
            {row.is_definition && (
              <Chip label="definition" size="small" variant="outlined" sx={{ height: 20 }} />
            )}
          </Stack>

          <TotalsSummary
            totals={row.totals}
            isDefinition={row.is_definition}
            columns={columns}
          />

          {row.ports.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: MONOSPACE, display: 'block' }}
            >
              {row.ports
                .map((port) => `${port.is_conjugate ? '~' : ''}${port.name} : ${port.type_name}`)
                .join('  ')}
            </Typography>
          )}
        </Stack>
      }
    >
      {node.children.map((child) => (
        <Branch
          key={child.key}
          node={child}
          selected={selected}
          onSelect={onSelect}
          onOpen={onOpen}
          columns={columns}
        />
      ))}
    </TreeItem>
  )
}
